import type { FormControlProps } from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type {
  FieldSuggestion,
  SuggestionSourceMetadata,
  PartViewerPageData,
} from '@frontend/api';
import {
  FormControl,
  FormLabel,
  Tooltip,
  InputGroupWithSuggestions,
  InputGroupWithSuggestionsInputType,
} from '@frontend/components';

import { getActiveFile } from 'modules/common/viewer/data/reducer';
import {
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  buildFieldSuggestionAnnotations,
} from 'modules/common/viewer/pdfViewer/annotationCommands';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';
import { getSuggestionAnnotationStateById } from 'modules/common/viewer/pdfViewer/data/reducer';
import { getViewerDisplayParameters } from 'modules/common/viewer/utils';
import {
  PART_DESCRIPTOR_LABEL_MAP,
  PART_DESCRIPTOR_TOOLTIP_MAP,
} from 'modules/quotePartViewer/constants';
import { getIsQuoteItemLocked } from 'modules/quotePartViewer/data/reducer';
import type { PartDescriptorKey } from 'modules/quotePartViewer/types';
import { DisabledPartDescriptorTooltipLabel } from 'modules/quotes/quoteItems/components/util';
import { partDetails } from 'modules/viewerPage/data/actions';
import {
  getIsInAFinalizedQuote,
  getIsInATrashedQuote,
  getIsModifiable,
  isPartDetailsSaving,
} from 'modules/viewerPage/data/reducer';

import { RootState } from 'store/configureStore';

export const useGetSuggestionProvenance = (
  suggestionsList: FieldSuggestion[],
) => {
  return useMemo(() => {
    const suggestionsProvenance: Record<
      string,
      FieldSuggestion<SuggestionSourceMetadata>[]
    > = {};
    [...suggestionsList].reverse().forEach((fieldSuggestion) => {
      const suggestion = (fieldSuggestion.suggestion as string).trim();
      if (!suggestionsProvenance[suggestion]) {
        suggestionsProvenance[suggestion] = [];
      }

      suggestionsProvenance[suggestion].push(
        fieldSuggestion as FieldSuggestion<SuggestionSourceMetadata>,
      );
    });

    return Object.entries(suggestionsProvenance).map(
      ([value, fieldSuggestions]) => {
        return {
          value: value,
          provenance: fieldSuggestions.map((fieldSuggestion) => ({
            filename: fieldSuggestion.sourceName,
            metadata: fieldSuggestion.sourceMetadata,
          })),
        };
      },
    );
  }, [suggestionsList]);
};

export const usePartDescriptorsUpdateWithSideEffects = ({
  partUuid,
  name,
  suggestions,
  initialValue,
  additionalOnSave,
}: {
  partUuid: string;
  name: PartDescriptorKey;
  suggestions: FieldSuggestion[];
  initialValue: string | null;
  additionalOnSave?: (
    name: PartDescriptorKey,
    newPartDescriptorValue: string | null,
  ) => void;
}) => {
  const dispatch = useDispatch();

  const internalAdditionalOnSave = useCallback(
    (newPartDescriptorValue: string | null) => {
      dispatch(
        partDetails.updateDescriptorsRequest({
          partUuid: partUuid,
          partDetails: { [name]: newPartDescriptorValue },
        }),
      );
      additionalOnSave?.(name, newPartDescriptorValue);
    },
    [name, partUuid, dispatch, additionalOnSave],
  );

  const inputSuggestions = useGetSuggestionProvenance(suggestions);

  const onSave = useCallback(
    (newPartDescriptorValue: string | null) => {
      if (initialValue == null && newPartDescriptorValue == null) {
        return;
      } else if (initialValue != newPartDescriptorValue) {
        internalAdditionalOnSave(newPartDescriptorValue);
      }
    },
    [initialValue, internalAdditionalOnSave],
  );

  const isLoading = useSelector(isPartDetailsSaving);

  return {
    onSave,
    inputSuggestions,
    isUpdatingPartDescriptors: isLoading,
  };
};

type PartDescriptorBaseProps = {
  name: PartDescriptorKey;
  activePart: PartViewerPageData;
  inputType?: InputGroupWithSuggestionsInputType;
  passedRef?: React.RefObject<HTMLDivElement>;
  suggestions: FieldSuggestion[];
  additionalOnSave?: (
    name: PartDescriptorKey,
    newPartDescriptorValue: string | null,
  ) => void;
  externalIsOpen?: boolean;
  externalOnOpen?: () => void;
  externalOnClose?: () => void;
} & Partial<FormControlProps>;

const PartDescriptorBase = ({
  name,
  activePart,
  inputType,
  passedRef,
  suggestions,
  additionalOnSave,
  externalIsOpen,
  externalOnOpen,
  externalOnClose,
  ...formControlProps
}: PartDescriptorBaseProps) => {
  const initialValue = activePart?.[name];
  const partUuid = activePart?.uuid;

  const { onSave, inputSuggestions } = usePartDescriptorsUpdateWithSideEffects({
    partUuid,
    name,
    suggestions,
    initialValue,
    additionalOnSave,
  });

  const partDescriptorRef = useRef<HTMLDivElement>(null);

  const isInAFinalizedQuote = useSelector(getIsInAFinalizedQuote);
  const isInATrashedQuote = useSelector(getIsInATrashedQuote);
  const isModifiable = useSelector(getIsModifiable);
  const isQuoteItemLocked = useSelector(getIsQuoteItemLocked);

  const canEditPartDescriptorData =
    !isQuoteItemLocked &&
    isModifiable &&
    !isInAFinalizedQuote &&
    !isInATrashedQuote;

  const tooltipLabel = (
    <DisabledPartDescriptorTooltipLabel
      action={`edit the ${PART_DESCRIPTOR_TOOLTIP_MAP[name]}`}
      isPartInSentQuote={isInAFinalizedQuote}
      isPartInTrashedQuote={isInATrashedQuote}
      isPartInMultipleQuoteItems={!isModifiable}
      isAnotherPersonEditing={isQuoteItemLocked}
    />
  );

  return (
    <FormControl
      ref={passedRef ?? partDescriptorRef}
      isDisabled={!canEditPartDescriptorData}
      {...formControlProps}
    >
      <FormLabel>{PART_DESCRIPTOR_LABEL_MAP[name]}</FormLabel>
      {
        // Conditionally render tooltip node, as tooltip w/ shouldWrapChildren and forwardRef do not work together
        !canEditPartDescriptorData ? (
          <Tooltip label={tooltipLabel} placement="right" shouldWrapChildren>
            <InputGroupWithSuggestions
              id={partUuid}
              inputType={inputType}
              initialValue={initialValue}
              suggestions={inputSuggestions}
              onSave={onSave}
              isDisabled={true}
              externalIsOpen={externalIsOpen}
              externalOnOpen={externalOnOpen}
              externalOnClose={externalOnClose}
            />
          </Tooltip>
        ) : (
          <InputGroupWithSuggestions
            id={partUuid}
            inputType={inputType}
            initialValue={initialValue}
            suggestions={inputSuggestions}
            ref={passedRef ?? partDescriptorRef}
            onSave={onSave}
            externalIsOpen={externalIsOpen}
            externalOnOpen={externalOnOpen}
            externalOnClose={externalOnClose}
          />
        )
      }
    </FormControl>
  );
};

// Version with annotation-controlled open state (for PDF viewer with suggestions on active file)
const PartDescriptorWithAnnotationControl = ({
  suggestions,
  ...props
}: Omit<
  PartDescriptorBaseProps,
  'externalIsOpen' | 'externalOnOpen' | 'externalOnClose'
>) => {
  const dispatch = useDispatch();

  // get flat list of annotations
  const suggestionAnnotationsArr = useMemo(() => {
    return buildFieldSuggestionAnnotations(suggestions);
  }, [suggestions]);

  // derive open/close state from viewer store: open if any related annotation is focused
  const isAnyAnnotationFocused = useSelector((state: RootState) => {
    return suggestionAnnotationsArr.some(({ id }) => {
      const annState = getSuggestionAnnotationStateById(state, id);
      return annState?.isFocused ?? false;
    });
  });

  const handleOpen = useCallback(() => {
    dispatch(
      suggestionAnnotations.queueCommands({
        commands: [
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.FOCUS,
            annotationIds: suggestionAnnotationsArr.map(({ id }) => id),
          },
        ],
      }),
    );
  }, [dispatch, suggestionAnnotationsArr]);

  const handleClose = useCallback(() => {
    dispatch(
      suggestionAnnotations.queueCommands({
        commands: [
          {
            type: SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS,
            annotationIds: suggestionAnnotationsArr.map(({ id }) => id),
          },
        ],
      }),
    );
  }, [dispatch, suggestionAnnotationsArr]);

  return (
    <PartDescriptorBase
      {...props}
      suggestions={suggestions}
      externalIsOpen={isAnyAnnotationFocused}
      externalOnOpen={handleOpen}
      externalOnClose={handleClose}
    />
  );
};

// Version with manual useDisclosure control (for non-PDF files or when active PDF has no suggestions)
const PartDescriptorWithManualControl = (
  props: Omit<
    PartDescriptorBaseProps,
    'externalIsOpen' | 'externalOnOpen' | 'externalOnClose'
  >,
) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <PartDescriptorBase
      {...props}
      externalIsOpen={isOpen}
      externalOnOpen={onOpen}
      externalOnClose={onClose}
    />
  );
};

type PartDescriptorWithSuggestionsFromPartProps = {
  name: PartDescriptorKey;
  quoteItemId: number;
  inputType?: InputGroupWithSuggestionsInputType;
  passedRef?: React.RefObject<HTMLDivElement>;
  additionalOnSave?: (
    name: PartDescriptorKey,
    newPartDescriptorValue: string | null,
  ) => void;
  activePart: PartViewerPageData;
  suggestions: FieldSuggestion[];
} & Partial<FormControlProps>;

// Used for Part Descriptors in the QPV - uses activePart to get/update part descriptor data + suggestions
export const PartDescriptorWithSuggestionsFromPart = ({
  name,
  quoteItemId,
  inputType,
  activePart,
  passedRef,
  additionalOnSave,
  suggestions,
  ...formControlProps
}: PartDescriptorWithSuggestionsFromPartProps) => {
  // Get active file to check if suggestions exist on it
  const activeFile = useSelector(getActiveFile);

  // Pre-compute: Do suggestions exist on the active file?
  const { shouldShowPdf, hasSuggestionsOnActiveFile } = useMemo(() => {
    if (!activeFile) {
      return { shouldShowPdf: false, hasSuggestionsOnActiveFile: false };
    }

    const viewerParams = getViewerDisplayParameters(activeFile);
    const activeFileUuid = activeFile.uuid;

    // Check if any suggestions have sourcePartFileId matching the active file
    const hasSuggestions = suggestions.some(
      (suggestion) => suggestion.sourcePartFileId === activeFileUuid,
    );

    return {
      shouldShowPdf: viewerParams.shouldShowPdf,
      hasSuggestionsOnActiveFile: hasSuggestions,
    };
  }, [activeFile, suggestions]);

  const commonProps = {
    name,
    activePart,
    inputType,
    passedRef,
    suggestions,
    additionalOnSave,
    ...formControlProps,
  };

  // Use annotation control only if PDF is showing AND suggestions exist on the active file
  // Otherwise use manual control
  return shouldShowPdf && hasSuggestionsOnActiveFile ? (
    <PartDescriptorWithAnnotationControl {...commonProps} />
  ) : (
    <PartDescriptorWithManualControl {...commonProps} />
  );
};
